import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TenantInfoService } from '@vsolv/core/multi-tenant/web';
import { API_BASE_URL } from '@vsolv/dev-kit/ngx';
import { FIREBASE_AUTH, idToken$ } from '@vsolv/packages/firebase/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { Claim } from '@wsphere/warranties/domain';
import { Auth } from 'firebase/auth';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class IntercomLinkService {
  private toastSvc = inject(ToastService);
  private apiBaseUrl = inject(API_BASE_URL) as string;
  private readonly intercomBaseUrl = 'https://app.intercom.com';
  private http = inject(HttpClient);
  private auth = inject(FIREBASE_AUTH) as Auth;
  private tenantInfo = inject(TenantInfoService);

  private async getAuthToken(): Promise<string | null> {
    return await firstValueFrom(idToken$(this.auth));
  }

  private async getTenantId(): Promise<string> {
    return this.tenantInfo.getLocalTenantId() || 'default';
  }

  async openCustomerInIntercom(claim: Claim.Model): Promise<void> {
    if (!claim?.warranty?.customer?.email) {
      this.toastSvc.show({
        type: 'error',
        title: 'Customer Information Missing',
        text: 'Customer email is required to view in Intercom.',
      });
      return;
    }

    try {
      // Resolve workspace app id from API
      const tenantId = await this.getTenantId();
      const customerEmail = claim.warranty.customer.email;
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await this.getAuthToken()}`,
        'x-vsolv-tid': tenantId,
        'x-api-mode': 'live',
      } as const;

      let workspaceAppId: string | undefined;
      let resolvedIntercomUserId = claim.warranty.customer.metadata?.['intercomUserId'];

      try {
        const data = await firstValueFrom(
          this.http.post<{ intercomUserId: string | null; appId?: string }>(
            `${this.apiBaseUrl}/v1/intercom/search-contact`,
            { email: customerEmail, tenantId },
            { headers }
          )
        );
        workspaceAppId = data?.appId || workspaceAppId;
        resolvedIntercomUserId = resolvedIntercomUserId || data?.intercomUserId || undefined;
      } catch (error) {
        // Ignore errors; fall back to generic Intercom page below
      }

      const appIdToUse = workspaceAppId;
      if (!appIdToUse) {
        this.toastSvc.show({
          type: 'error',
          title: 'Intercom Configuration',
          text: 'Unable to determine Intercom workspace for this tenant.',
        });
        return;
      }

      if (resolvedIntercomUserId) {
        const intercomUrl = `${this.intercomBaseUrl}/a/apps/${appIdToUse}/users/${resolvedIntercomUserId}/all-conversations`;
        window.open(intercomUrl, '_blank');
        return;
      }

      const intercomUrl = `${this.intercomBaseUrl}/a/apps/${appIdToUse}/users/segments/all-users`;
      window.open(intercomUrl, '_blank');
    } catch {
      this.toastSvc.show({
        type: 'error',
        title: 'Configuration Error',
        text: 'Unable to open Intercom link.',
      });
    }
  }
}
